//@flow
import React, {useState, useEffect} from 'react';
import {Link} from 'react-router-dom';
import {Typography, makeStyles} from '@material-ui/core';

import {AddCircleOutline, Edit, Info, LocalAtm} from '@material-ui/icons';
import {colors} from '../../../assets/styles/colors';

import type {
  Order,
  OrderTaxInvoiceInformation,
  OrderTaxInvoiceData,
} from '../../../entities';

import {
  ORDER_STATES_FOR_TAX_INVOICE,
  TAX_INVOICE_STATE_LABELS,
} from '../../../entities/tax-invoice';
import CustomModal from '../../../elements/CustomModal/CustomModal';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {useOrderTaxInvoiceOrderActions} from '../../tax-invoice/utils';
import InlineLoader from '../../../elements/LoadingSpinner/InlineLoader';
import {useAlert} from '../../../elements/Alert/AlertContext';
import {useOrdersActions} from '../utils';
import TiendadaTooltip from '../../../elements/tiendada-tooltip';

type Props = {
  order: Order,
  taxInvoiceList: OrderTaxInvoiceData[],
  taxInvoiceInformation?: OrderTaxInvoiceInformation,
};

const useStyles = makeStyles(theme => ({
  createDocumentButton: {
    fontSize: '0.8rem',
    color: colors.grey700,
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    border: `2px dashed ${colors.grey300}`,
    padding: '.25rem .5rem',
    borderRadius: '.5rem',
    width: '100%',

    '&:hover': {
      color: colors.primary,
    },

    '&:disabled': {
      color: colors.grey500,
      cursor: 'not-allowed',
    },
  },
  modalIcon: {
    fontSize: '1.25rem',
    width: 100,
    height: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    margin: '1rem auto',
    background: colors.info,
    color: 'white',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '1rem',
    fontWeight: 400,
    marginBottom: theme.spacing(1),
    textAlign: 'center',
  },
  createDocumentLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontWeight: 500,
  },
  invoiceBadge: {
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: colors.grey200,
    borderRadius: '4px',
    gap: '0.5rem',
    padding: '4px .45rem',
  },
  invoiceLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8rem',
    color: colors.grey700,
    gap: '0.25rem',
    paddingRight: '0.25rem',
    borderRight: `1px dashed ${colors.grey300}`,
    fontWeight: 500,
  },
  invoiceValue: {
    fontWeight: 500,
    color: colors.primary,
    whiteSpace: 'nowrap',

    '&[data-state="SCHEDULED"]': {
      color: colors.warning,
    },
    '&[data-state="ACCEPTED"]': {
      color: colors.success,
    },

    '&[data-state="CANCELED"], &[data-state="REJECTED"], &[data-state="FAILED"]': {
      color: colors.error,
    },

    '&[data-state="PENDING"]': {
      color: colors.info,
    },
  },
  modalContainer: {
    display: 'flex',
    maxWidth: 600,
    margin: '0 auto',
  },
  button: {
    padding: '10px 2px',
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    alignItems: 'center',
    width: '100%',
    margin: '1rem 0',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    position: 'relative',
    width: '100%',
  },
}));

export default function InvoiceDocumentBadge({
  order,
  taxInvoiceList,
  taxInvoiceInformation,
}: Props) {
  const classes = useStyles();

  const {showAlert} = useAlert();

  const [redirectModalOpen, setRedirectModalOpen] = useState(false);

  const [innerTaxInvoiceList, setInnerTaxInvoiceList] = useState<
    OrderTaxInvoiceData[]
  >(taxInvoiceList || []);

  const {
    generateOrderTaxInvoice,
    loading,
    error,
  } = useOrderTaxInvoiceOrderActions();
  const {updateOrderTaxInvoice} = useOrdersActions();

  const canEmitDocument =
    !!taxInvoiceInformation &&
    ORDER_STATES_FOR_TAX_INVOICE.includes(order?.orderState);

  const hasTaxes = order?.paymentInvoice?.taxes?.length > 0;

  const [lastTaxInvoice] = innerTaxInvoiceList;

  useEffect(() => {
    if (error) {
      showAlert(error, {
        type: 'error',
      });
    }
  }, [error, showAlert]);

  useEffect(() => {
    setInnerTaxInvoiceList(taxInvoiceList);
  }, [taxInvoiceList]);

  const handleClickEmitDocument = () => {
    if (!canEmitDocument) {
      showAlert(
        "Solo se puede emitir una factura a partir del estado 'Pagado'",
        {
          type: 'warning',
        }
      );
      return;
    }

    generateOrderTaxInvoice(order.id).then(response => {
      if (!response) return;
      setInnerTaxInvoiceList(prevList => [...prevList, response]);
      updateOrderTaxInvoice(order.id, [response]);
    });
  };

  if (!hasTaxes) {
    return (
      <div className={classes.invoiceBadge}>
        <span className={classes.invoiceLabel}>Sin Impuestos</span>
      </div>
    );
  }

  const hasTaxInvoice = innerTaxInvoiceList.length > 0;
  const hasInformationForTaxInvoice = !!taxInvoiceInformation;

  const emitButtonTooltipTitle = canEmitDocument
    ? 'Inicia el proceso de facturación'
    : 'Solo se puede emitir una factura a partir del estado Pagado';

  if (!hasTaxInvoice) {
    if (!hasInformationForTaxInvoice) {
      return (
        <div
          onClick={e => e.stopPropagation()}
          className={classes.buttonContainer}
        >
          <TiendadaTooltip
            placement="top"
            title="Completa datos de facturación"
          >
            <button
              className={classes.createDocumentButton}
              disabled={loading}
              onClick={e => {
                e.stopPropagation();
                setRedirectModalOpen(true);
              }}
            >
              <div className={classes.createDocumentLabel}>
                <Edit fontSize="small" />
                <span className={classes.createDocumentLabel}>
                  Preparar documento
                </span>
              </div>
            </button>
          </TiendadaTooltip>
          <CustomModal
            open={redirectModalOpen}
            className={classes.modalContainer}
            handleCloseModal={() => setRedirectModalOpen(false)}
          >
            <div className={classes.modalContent}>
              <span className={classes.modalIcon}>
                <Info fontSize="large" />
              </span>
              <Typography className={classes.title}>
                ¡Un último paso!
              </Typography>
              <Typography className={classes.subtitle}>
                Para continuar y emitir tu documento, necesitamos tus datos de
                facturación.
              </Typography>
              <footer className={classes.options}>
                <CustomButton
                  className={classes.button}
                  variant="contained"
                  color="default"
                  onClick={() => setRedirectModalOpen(false)}
                  fullWidth
                >
                  Cancelar
                </CustomButton>

                <CustomButton
                  className={classes.button}
                  to={`/orders/${order.id}`}
                  component={Link}
                  onClick={() => setRedirectModalOpen(false)}
                  variant="contained"
                  fullWidth
                >
                  Completar datos
                </CustomButton>
              </footer>
            </div>
          </CustomModal>
        </div>
      );
    }

    return (
      <div
        onClick={e => e.stopPropagation()}
        className={classes.buttonContainer}
      >
        <TiendadaTooltip placement="top" title={emitButtonTooltipTitle}>
          <button
            className={classes.createDocumentButton}
            disabled={loading || !canEmitDocument}
            onClick={e => {
              e.stopPropagation();
              handleClickEmitDocument();
            }}
          >
            {loading ? (
              <InlineLoader loading size={20} />
            ) : (
              <div className={classes.createDocumentLabel}>
                <AddCircleOutline fontSize="small" />

                <span className={classes.createDocumentLabel}>
                  Emitir {taxInvoiceInformation?.taxInvoiceType?.label}
                </span>
              </div>
            )}
          </button>
        </TiendadaTooltip>
        <CustomModal
          open={redirectModalOpen}
          className={classes.modalContainer}
          handleCloseModal={() => setRedirectModalOpen(false)}
        >
          <div className={classes.modalContent}>
            <span className={classes.modalIcon}>
              <Info fontSize="large" />
            </span>
            <Typography className={classes.title}>¡Un último paso!</Typography>
            <Typography className={classes.subtitle}>
              Para continuar y emitir tu documento, necesitamos tus datos de
              facturación.
            </Typography>
            <footer className={classes.options}>
              <CustomButton
                className={classes.button}
                variant="contained"
                color="default"
                onClick={() => setRedirectModalOpen(false)}
                fullWidth
              >
                Cancelar
              </CustomButton>

              <CustomButton
                className={classes.button}
                to={`/orders/${order.id}`}
                component={Link}
                onClick={() => setRedirectModalOpen(false)}
                variant="contained"
                fullWidth
              >
                Completar datos
              </CustomButton>
            </footer>
          </div>
        </CustomModal>
      </div>
    );
  }

  return (
    <div className={classes.invoiceBadge}>
      <span className={classes.invoiceLabel}>
        <LocalAtm fontSize="small" />
        <span>{lastTaxInvoice.taxInvoiceType.label}</span>
      </span>
      <span className={classes.invoiceValue} data-state={lastTaxInvoice.state}>
        {TAX_INVOICE_STATE_LABELS[lastTaxInvoice.state]}
      </span>
    </div>
  );
}
